//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import {
  IGalleriesPinnedMediaDismissedAlerts,
  IGetUserSettingsResponse,
} from '@apis/user-settings/userSettingsApiModel';
import {
  IContentAcquisitionUserSettings,
  IContentDiscoveryFirstRunStatus,
  IUserSettings,
} from '@models/IUserSettings';
import { gatewayFetch } from '@utils/sessionFetch';

const userSettingsFetch = (endpoint: string, init?: RequestInit) => {
  return gatewayFetch('web_gateway', `/api/v1/user-settings${endpoint}`, init);
};

/**
 *
 * Gets the current user's Curalate settings
 *
 * /api/v1/user-settings/
 *
 */
export const getUserSettings = async (
  init?: RequestInit,
): Promise<IUserSettings> => {
  const res = await userSettingsFetch('/', init);
  const settingsResponse: IGetUserSettingsResponse = await res.json();
  return settingsResponse.settings;
};

/**
 *
 * Overrides the current user's settings with the given values
 *
 * /api/v1/user-settings/
 *
 */
export const setUserSettings = async (
  settings: Partial<IUserSettings>,
): Promise<void> => {
  await userSettingsFetch('/', {
    method: 'PUT',
    body: JSON.stringify({
      settings,
    }),
  });
};

/**
 * @returns The user's current content acquisition settings
 */
export const getContentAcquisitionUserSettings =
  async (): Promise<IContentAcquisitionUserSettings> => {
    const settings = await getUserSettings();
    return settings.contentAcquisition;
  };

/**
 * @param settingsParams The current user's new content acquisition settings
 */
export const setContentAcquisitionUserSettings = async (
  settingsParams: IContentAcquisitionUserSettings,
): Promise<void> => {
  await setUserSettings({ contentAcquisition: settingsParams });
};

/**
 * Gets the current user's content discovery first run status
 */
export const getContentDiscoveryFirstRunStatus =
  async (): Promise<IContentDiscoveryFirstRunStatus> => {
    const settings = await getUserSettings();
    return settings.contentDiscoveryFirstRunStatus;
  };

/**
 * @param settingsParams The current user's new content discovery first run status
 */
export const setContentDiscoveryFirstRunStatus = async (
  settingsParams: IContentDiscoveryFirstRunStatus,
): Promise<void> => {
  await setUserSettings({ contentDiscoveryFirstRunStatus: settingsParams });
};

/**
 * Gets the current user's pinned media-related dismissed alert status
 */
export const getGalleriesPinnedMediaDismissedAlerts =
  async (): Promise<IGalleriesPinnedMediaDismissedAlerts> => {
    const settings = await getUserSettings();

    return {
      cannotPinPdpMedia: settings.galleriesPinnedMediaCannotPinAlert,
      dragAndDropMedia: settings.galleriesPinnedMediaDragAndDropAlert,
    };
  };

/**
 * @param settingsParams The current user's new pinned media dismissed alerts
 */
export const setGalleriesPinnedMediaDismissedAlerts = async (
  settingsParams: IGalleriesPinnedMediaDismissedAlerts,
): Promise<void> => {
  await setUserSettings({
    galleriesPinnedMediaCannotPinAlert: settingsParams.cannotPinPdpMedia,
    galleriesPinnedMediaDragAndDropAlert: settingsParams.dragAndDropMedia,
  });
};
